import { useEffect } from 'react';
import debounce from 'lodash.debounce';
import { LIST_ACTIONS } from 'lib/list-helper/makeListReducer';
import useQueryList from 'lib/list-helper/useQueryList';
import useConfirm from 'lib/components/useConfirm/useConfirm';
import useNavigationPrompt from 'lib/useNavigationPrompt';
import dynamicModificationRulesService from 'lib/dynamicModificationRulesService';
import {
  DYNAMIC_RULES_ACTIONS,
  initialState,
  reducer,
} from './settingsDynamicRulesReducer';
import {
  emptyRule,
  formToApi,
  RULE_PRESETS,
  ruleToForm,
  validateRuleForm,
} from './dynamicRuleStages';

const fetchRules = debounce((params, dispatch) => {
  dispatch({ type: LIST_ACTIONS.SET_LOADING, payload: true });

  return dynamicModificationRulesService
    .getRules(params)
    .then((results) => {
      dispatch({ type: LIST_ACTIONS.APP_LOADS_DATA, payload: { ...results } });
    })
    .catch(() => {
      dispatch({
        type: LIST_ACTIONS.SET_ERRORS,
        payload: { errors: ['The rules could not be loaded. Please refresh the page.'] },
      });
    })
    .then(() => dispatch({ type: LIST_ACTIONS.SET_LOADING, payload: false }));
}, 400);

const useSettingsDynamicRules = ({ location, history }) => {
  const { state, dispatch, setSearch, setPage, setPageSize } = useQueryList(
    location,
    history,
    reducer,
    initialState,
  );
  const { confirm } = useConfirm();

  useNavigationPrompt(state.modal.isOpen && state.modal.isDirty);

  const listParams = {
    search: state.search,
    page: state.page,
    pageSize: state.pageSize,
  };

  useEffect(() => {
    fetchRules(listParams, dispatch);
  }, [state.search, state.page, state.pageSize]);

  const userOpensNewRule = () =>
    dispatch({ type: DYNAMIC_RULES_ACTIONS.USER_OPENS_RULE, payload: emptyRule() });

  const userOpensRule = (rule) =>
    dispatch({ type: DYNAMIC_RULES_ACTIONS.USER_OPENS_RULE, payload: ruleToForm(rule) });

  const userClosesRule = () => dispatch({ type: DYNAMIC_RULES_ACTIONS.USER_CLOSES_RULE });

  const userChangesRule = (key, value) =>
    dispatch({ type: DYNAMIC_RULES_ACTIONS.USER_CHANGES_RULE, payload: { key, value } });

  const userChangesStage = (index, changes) =>
    dispatch({ type: DYNAMIC_RULES_ACTIONS.USER_CHANGES_STAGE, payload: { index, changes } });

  const userAddsStage = () => dispatch({ type: DYNAMIC_RULES_ACTIONS.USER_ADDS_STAGE });

  const userRemovesStage = (index) =>
    dispatch({ type: DYNAMIC_RULES_ACTIONS.USER_REMOVES_STAGE, payload: index });

  const userAppliesPreset = (presetValue) => {
    const preset = RULE_PRESETS.find((option) => option.value === presetValue);
    if (preset) {
      dispatch({ type: DYNAMIC_RULES_ACTIONS.USER_APPLIES_PRESET, payload: preset.build() });
    }
  };

  const userSavesRule = () => {
    const { rule } = state.modal;
    const errors = validateRuleForm(rule);
    if (Object.keys(errors).length) {
      dispatch({ type: DYNAMIC_RULES_ACTIONS.APP_FAILS_SAVING, payload: errors });
      return;
    }

    dispatch({ type: DYNAMIC_RULES_ACTIONS.APP_STARTS_SAVING });
    dynamicModificationRulesService
      .saveRule({ ...formToApi(rule), id: rule.id })
      .then(() => {
        dispatch({ type: DYNAMIC_RULES_ACTIONS.USER_CLOSES_RULE });
        fetchRules(listParams, dispatch);
      })
      .catch((apiErrors) =>
        dispatch({ type: DYNAMIC_RULES_ACTIONS.APP_FAILS_SAVING, payload: apiErrors }),
      );
  };

  const userDeletesRule = (rule) =>
    confirm({
      title: 'Delete rule?',
      body: rule.templatesCount
        ? `"${rule.name}" is used on ${rule.templatesCount} question(s). Those questions will be asked every time again.`
        : `"${rule.name}" will be deleted.`,
      confirmText: 'Delete',
    }).then((isConfirmed) => {
      if (!isConfirmed) return;
      dynamicModificationRulesService
        .deleteRule(rule.id)
        .then(() => fetchRules(listParams, dispatch));
    });

  return {
    state,
    setSearch,
    setPage,
    setPageSize,
    userOpensNewRule,
    userOpensRule,
    userClosesRule,
    userChangesRule,
    userChangesStage,
    userAddsStage,
    userRemovesStage,
    userAppliesPreset,
    userSavesRule,
    userDeletesRule,
  };
};

export default useSettingsDynamicRules;
